import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const MarketingGoalsSection = ({ formData, errors, onChange }) => {
  const objectiveOptions = [
    { value: 'brand-awareness', label: 'Brand Awareness', description: 'Increase visibility and recognition' },
    { value: 'lead-generation', label: 'Lead Generation', description: 'Capture qualified prospects' },
    { value: 'customer-acquisition', label: 'Customer Acquisition', description: 'Convert leads to paying customers' },
    { value: 'customer-retention', label: 'Customer Retention', description: 'Reduce churn and increase loyalty' },
    { value: 'engagement', label: 'Engagement', description: 'Boost interaction and community building' },
    { value: 'sales-revenue', label: 'Sales & Revenue', description: 'Drive direct sales and revenue growth' },
    { value: 'thought-leadership', label: 'Thought Leadership', description: 'Establish industry authority' },
    { value: 'product-launch', label: 'Product Launch', description: 'Promote new products or features' }
  ];

  const budgetOptions = [
    { value: 'minimal', label: 'Minimal (<$1,000/month)', description: 'Bootstrap marketing with limited budget' },
    { value: 'small', label: 'Small ($1,000-$5,000/month)', description: 'Focused campaigns with modest spend' },
    { value: 'medium', label: 'Medium ($5,000-$20,000/month)', description: 'Multi-channel marketing efforts' },
    { value: 'large', label: 'Large ($20,000-$100,000/month)', description: 'Comprehensive marketing programs' },
    { value: 'enterprise', label: 'Enterprise ($100,000+/month)', description: 'Full-scale marketing operations' }
  ];

  const metricsOptions = [
    { value: 'website-traffic', label: 'Website Traffic', description: 'Unique visitors, page views' },
    { value: 'conversion-rate', label: 'Conversion Rate', description: 'Lead-to-customer conversion' },
    { value: 'cac', label: 'Customer Acquisition Cost (CAC)', description: 'Cost per new customer' },
    { value: 'roi', label: 'Return on Investment (ROI)', description: 'Revenue generated vs. spend' },
    { value: 'engagement', label: 'Engagement Metrics', description: 'Likes, shares, comments, time on site' },
    { value: 'email-performance', label: 'Email Performance', description: 'Open rates, click-through rates' },
    { value: 'social-reach', label: 'Social Media Reach', description: 'Followers, impressions, reach' },
    { value: 'brand-sentiment', label: 'Brand Sentiment', description: 'Customer perception and reviews' }
  ];

  const handleObjectiveChange = (value, checked) => {
    const current = formData?.marketingObjectives || [];
    const updated = checked
      ? [...current, value]
      : current?.filter(v => v !== value);
    onChange('marketingObjectives', updated);
  };

  const handleMetricChange = (value, checked) => {
    const current = formData?.successMetrics || [];
    const updated = checked
      ? [...current, value]
      : current?.filter(v => v !== value);
    onChange('successMetrics', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Target" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground font-heading">
            Marketing Goals Assessment
          </h2>
          <p className="text-muted-foreground mt-1">
            Define your marketing objectives and success criteria to align AI agent strategies
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Primary Marketing Objectives
            <span className="text-error ml-1">*</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Select your top marketing goals (choose at least one)
          </p>
          <div className="space-y-3">
            {objectiveOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                description={option?.description}
                checked={(formData?.marketingObjectives || [])?.includes(option?.value)}
                onChange={(e) => handleObjectiveChange(option?.value, e?.target?.checked)}
              />
            ))}
          </div>
          {errors?.marketingObjectives && (
            <p className="text-sm text-error mt-2">{errors?.marketingObjectives}</p>
          )}
        </div>

        <div>
          <Select
            label="Monthly Marketing Budget"
            description="Approximate budget allocated for marketing activities"
            placeholder="Select budget range"
            options={budgetOptions}
            value={formData?.budgetAllocation || ''}
            onChange={(value) => onChange('budgetAllocation', value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Success Metrics
            <span className="text-muted-foreground ml-2 font-normal">(Select all that apply)</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Which metrics will you track to measure marketing success?
          </p>
          <div className="space-y-3">
            {metricsOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                description={option?.description}
                checked={(formData?.successMetrics || [])?.includes(option?.value)}
                onChange={(e) => handleMetricChange(option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-start gap-2">
            <Icon name="TrendingUp" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Goal-Aligned Content</p>
              <p className="text-xs text-muted-foreground">
                Marketing Agent will create campaigns optimized for your specific objectives
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-success/10 rounded-lg border border-success/20">
          <div className="flex items-start gap-2">
            <Icon name="BarChart3" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Performance Tracking</p>
              <p className="text-xs text-muted-foreground">
                Feedback Agent will monitor your selected metrics and provide insights
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingGoalsSection;